import type { CountKind, RowSide } from './nursery';

/**
 * What a walk of the rows is for. A stocktake checks the record; a bud, 1st or
 * 2nd count is a step in a growing crop's season and feeds the forecast.
 */
export type StocktakeKind = 'stocktake' | CountKind;

export const STOCKTAKE_KIND_NAME: Record<StocktakeKind, string> = {
  stocktake: 'Stocktake',
  bud_count: 'Bud count',
  first_count: '1st count',
  second_count: '2nd count',
};

/** A farm, or a block of it, with lots standing there to count. */
export interface StocktakePlace {
  farm_id: number;
  farm_name: string;
  lots: number;
  trees: number;
  blocks: { block_id: number; block_name: string; sort_order: number | null; lots: number; trees: number }[];
}

export type StocktakeStatus = 'open' | 'finished';

export interface Stocktake {
  id: number;
  stocktake_number: number;
  label: string;
  farm_id: number;
  farm_name: string;
  block_id: number | null;
  block_name: string | null;
  status: StocktakeStatus;
  /** Older servers send none: a stocktake. */
  kind?: StocktakeKind;
  kind_name?: string;
  notes: string | null;
  started_at: string;
  started_by_name: string | null;
  finished_at: string | null;
  finished_by_name: string | null;
}

export interface StocktakeListItem extends Stocktake {
  lots_counted: number;
  lots_total: number;
  difference: number;
}

export interface StocktakesResponse {
  /** null: every farm. A number: an Order Picker's farms. */
  farms_assigned: number | null;
  places: StocktakePlace[];
  stocktakes: StocktakeListItem[];
}

export interface StocktakeCount {
  id: number;
  shown_count: number;
  counted_count: number;
  count_before: number;
  count_after: number;
  note: string | null;
  counted_at: string;
  counted_by_name: string | null;
}

export interface StocktakeLot {
  lot_id: number;
  lot_code: string | null;
  current_count: number;
  lot_status: string;
  current_stage: string | null;
  last_count_kind?: CountKind | null;
  last_counted_at?: string | null;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  location_id: number | null;
  location_name: string | null;
  row_number: number | null;
  row_side: RowSide | null;
  sequence_number: number | null;
  block_id: number | null;
  block_name: string | null;
  block_sort_order: number | null;
  farm_name: string | null;
  count: StocktakeCount | null;
}

export interface StocktakeSummary {
  lots_total: number;
  lots_counted: number;
  trees_shown: number;
  trees_counted: number;
  lots_over: number;
  lots_under: number;
  lots_matching: number;
  difference: number;
}

export interface StocktakeDetail {
  stocktake: Stocktake;
  lots: StocktakeLot[];
  summary: StocktakeSummary;
}

export interface StocktakeCountPayload {
  lot_id: number;
  shown: number;
  counted: number;
  note?: string | null;
  client_uuid: string;
  counted_at: string;
}

/** The raw row the server recorded. */
export interface StocktakeCountRecord {
  id: number;
  lot_id: number;
  shown_count: number;
  counted_count: number;
  count_before: number;
  count_after: number;
  note: string | null;
  counted_at: string;
}
